import { useLayoutEffect, useState } from 'react';

import { GoogleApiConfiguration } from '@savgroup-front-common/configuration';

export const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script';

const markScriptLoaded = (script: HTMLScriptElement): void => {
  script.dataset.loaded = 'true';
};

const loadPlacesLibrary = (): Promise<void> => {
  const importLibrary = window.google?.maps?.importLibrary;

  if (!importLibrary) {
    return Promise.resolve();
  }

  return importLibrary('places').then(() => undefined);
};

let placesReady: Promise<void> | undefined;

const waitForPlaces = (script: HTMLScriptElement): Promise<void> => {
  if (script.dataset.loaded === 'true') {
    return loadPlacesLibrary();
  }

  if (!placesReady) {
    placesReady = new Promise<void>((resolve) => {
      script.addEventListener('load', () => resolve(), { once: true });
    })
      .then(() => loadPlacesLibrary())
      .then(() => {
        markScriptLoaded(script);
      })
      .catch((error: unknown) => {
        placesReady = undefined;

        throw error;
      });
  }

  return placesReady;
};

const useInitGoogleMap = (): { isLoading: boolean } => {
  const [isLoading, setIsLoading] = useState(true);

  useLayoutEffect(() => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    const script =
      existing instanceof HTMLScriptElement ? existing : document.createElement('script');

    if (!(existing instanceof HTMLScriptElement)) {
      placesReady = undefined;
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GoogleApiConfiguration.apiKey}&libraries=places&v=weekly&loading=async`;
      script.type = 'text/javascript';
      script.async = true;
    }

    let isActive = true;
    const ready = waitForPlaces(script);

    if (!(existing instanceof HTMLScriptElement)) {
      document.body.appendChild(script);
    }

    void ready.finally(() => {
      if (isActive) {
        setIsLoading(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, []);

  return { isLoading };
};

export default useInitGoogleMap;
